import {
    DataTable,
    Mono,
    StatusBadge,
    Switch,
    Text,
    Tooltip,
    TooltipContent,
    TooltipPortal,
    TooltipProvider,
    TooltipTrigger,
    UserCell,
    type DataTableColumn,
} from "@athena/ui";
import { Info } from "lucide-react";
import type { Role, UserStatus } from "@athena/common/graphql/generated/schema";
import { roleLabelMap, roleVariantMap } from "@/types/Roles";
import { STATUS_LABEL, STATUS_VARIANT } from "@/types/Status";
import { formatDateTime } from "../../../../global/formatHelpers";

/**
 * The users table, as a set of column definitions rather than a fixed table.
 *
 * Three surfaces list people and none of them wants the same columns: the platform directory shows
 * organization, role and status; an organization's members tab shows phone, license and when they
 * joined; an assignment's learners show due date and progress. A single component covering all three
 * would need a flag per difference — so instead the *columns* are the reusable unit, and each page
 * names the ones it wants.
 *
 * Everything a person's row can show lives here once. Change how a role renders, or what the identity
 * cell looks like, and all three surfaces change together.
 */

/** The shape any of these columns may read. Pages pass rows that satisfy the columns they picked. */
export interface UserRow {
    /** The *person*. Seeds the avatar color, so one learner is the same color in every list. */
    id: string;
    /**
     * Row identity where it is not the person — one learner can hold several assignments, so keying
     * those rows on the user would collapse them.
     */
    rowId?: string;
    firstName?: string | null;
    lastName?: string | null;
    /** For callers that only ever had a joined name. */
    name?: string | null;
    email: string;
    phoneNumber?: string | null;
    role?: Role;
    status?: UserStatus;
    lastLogin?: string | null;
    /** Later of last login and latest action of their own, email opens included (ATH-243). */
    lastActiveAt?: string | null;
    /** First mobile sign-in, our stand-in for "has the app". Null means never seen on mobile. */
    mobileFirstLoginAt?: string | null;
    createdAt?: string | null;
    joinedAt?: string | null;
    licensed?: boolean;
    /** Whether an admin has barred this person from age-restricted content (ADR 0010). */
    ageRestricted?: boolean;
    organizationName?: string | null;
    /** Free-form per-surface values the `text` column reads. */
    dueDate?: string | null;
    progress?: string | null;
    subOrganizationName?: string | null;
    /** Rendered under the email. Reserved for a third line that genuinely belongs to identity. */
    meta?: React.ReactNode;
    /**
     * Replaces the status cell. For a row that has no account and therefore no `status` — a pending
     * invitation — where "Invited" is the status, not a note about the person.
     *
     * It lived under the email briefly and that was wrong twice over: it made one row three lines tall
     * while its neighbors were two, lifting the avatar out of alignment, and it left the Status column
     * showing a dash next to it.
     */
    statusNode?: React.ReactNode;
    /** Pending auto-enrollment: visible in a directory, but has no profile to open or edit yet. */
    isPendingInvite?: boolean;
}

/**
 * The age-restriction column's header, with what the setting actually does.
 *
 * A toggle labeled only "Age restricted" reads as either "they are under 18" or "they may see
 * adult material" depending on who is looking, and the two are opposite instructions. The
 * explanation is here rather than in a doc nobody opens next to the switch they are about to flip.
 *
 * A real `button` rather than a hoverable icon: a tooltip only reachable with a mouse is not
 * reachable at all for somebody on a keyboard.
 */
const AgeRestrictedHeader = () => (
    <span className="inline-flex items-center gap-1.5">
        Age restricted
        <TooltipProvider>
            {/* `delayDuration={0}` and a portal, matching ContentTile: a help bubble somebody is
                reaching for should not make them hold still for it, and the portal keeps it out of
                the table's overflow. */}
            <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                    <button
                        type="button"
                        aria-label="What does age restricted mean?"
                        className="hover:bg-border rounded-sm p-0.5"
                    >
                        <Info className="text-muted-foreground h-3.5 w-3.5" />
                    </button>
                </TooltipTrigger>
                <TooltipPortal>
                    <TooltipContent
                        side="bottom"
                        align="start"
                        className="max-w-64 rounded-lg border-0 text-xs font-normal wrap-break-word whitespace-normal shadow-lg"
                        style={{ backgroundColor: "var(--foreground)", color: "var(--background)" }}
                    >
                        Blocks this person from content marked as age restricted. It is hidden from them rather than
                        shown as locked — there is nothing they can do to unlock it.
                    </TooltipContent>
                </TooltipPortal>
            </Tooltip>
        </TooltipProvider>
    </span>
);

const dash = (
    <Text muted className="text-sm">
        —
    </Text>
);

/**
 * Every column a user row can have, keyed by name.
 *
 * Functions where a column needs something from the page — toggling a license is a mutation the page
 * owns — and plain objects where it does not.
 */
export const userColumns = {
    identity: {
        key: "identity",
        header: "User",
        cell: (user: UserRow) => (
            <UserCell
                id={user.id}
                firstName={user.firstName}
                lastName={user.lastName}
                name={user.name}
                email={user.email}
                meta={user.meta}
            />
        ),
    } satisfies DataTableColumn<UserRow>,

    organization: {
        key: "organization",
        header: "Organization",
        cell: (user: UserRow) =>
            user.organizationName ? (
                <div>
                    <Text size="sm">{user.organizationName}</Text>
                    {user.subOrganizationName && (
                        <Mono size="xs" muted>
                            {user.subOrganizationName}
                        </Mono>
                    )}
                </div>
            ) : (
                dash
            ),
    } satisfies DataTableColumn<UserRow>,

    phone: {
        key: "phone",
        header: "Phone number",
        cell: (user: UserRow) => (user.phoneNumber ? <Mono size="xs">{user.phoneNumber}</Mono> : dash),
    } satisfies DataTableColumn<UserRow>,

    role: {
        key: "role",
        header: "Role",
        cell: (user: UserRow) =>
            user.role ? <StatusBadge variant={roleVariantMap[user.role]}>{roleLabelMap[user.role]}</StatusBadge> : dash,
    } satisfies DataTableColumn<UserRow>,

    status: {
        key: "status",
        header: "Status",
        cell: (user: UserRow) =>
            user.statusNode ? (
                user.statusNode
            ) : user.status ? (
                <StatusBadge variant={STATUS_VARIANT[user.status]}>{STATUS_LABEL[user.status]}</StatusBadge>
            ) : (
                dash
            ),
    } satisfies DataTableColumn<UserRow>,

    lastActive: {
        key: "lastActive",
        header: "Last active",
        cell: (user: UserRow) =>
            user.lastActiveAt ? <Text size="sm">{formatDateTime(user.lastActiveAt)}</Text> : dash,
    } satisfies DataTableColumn<UserRow>,

    /**
     * When this person first opened the app.
     *
     * The dash is load-bearing here in a way it is not in the other columns: null means they have
     * never signed in on mobile, which is the answer somebody scanning this column is looking for,
     * not missing data.
     */
    appDownloaded: {
        key: "appDownloaded",
        header: "App downloaded",
        cell: (user: UserRow) =>
            user.mobileFirstLoginAt ? <Text size="sm">{formatDateTime(user.mobileFirstLoginAt)}</Text> : dash,
    } satisfies DataTableColumn<UserRow>,

    joined: {
        key: "joined",
        header: "Joined",
        cell: (user: UserRow) => {
            const when = user.joinedAt ?? user.createdAt;
            return when ? <Text size="sm">{formatDateTime(when)}</Text> : dash;
        },
    } satisfies DataTableColumn<UserRow>,

    /** A free-text column the page supplies — a due date, a progress string. */
    text: (key: string, header: React.ReactNode, value: (user: UserRow) => string | null | undefined) =>
        ({
            key,
            header,
            cell: (user: UserRow) => {
                const text = value(user);
                return text ? <Text className="max-w-55 truncate text-sm">{text}</Text> : dash;
            },
        }) satisfies DataTableColumn<UserRow>,

    /** A status the page labels itself — assignment progress is not the account's own status. */
    badge: (key: string, header: React.ReactNode, render: (user: UserRow) => React.ReactNode) =>
        ({ key, header, cell: render }) satisfies DataTableColumn<UserRow>,

    /**
     * A seat, not a membership — the two are deliberately separate (ADR 0009), so this is a toggle
     * rather than a read-only badge. `pending` disables the one row being written rather than the
     * whole table.
     */
    licensed: (onToggle: (user: UserRow, licensed: boolean) => void, pendingId?: string | null) =>
        ({
            key: "licensed",
            header: "Licensed",
            cell: (user: UserRow) =>
                user.licensed === undefined ? (
                    dash
                ) : (
                    <Switch
                        checked={user.licensed}
                        disabled={pendingId === user.id}
                        aria-label={`Licensed — ${user.firstName ?? user.email}`}
                        onCheckedChange={(next) => onToggle(user, next)}
                    />
                ),
        }) satisfies DataTableColumn<UserRow>,

    /**
     * Barred from age-restricted content, or not. A toggle for the same reason `licensed` is one:
     * it is a setting an admin flips from the roster, not a fact about the person to read back.
     *
     * A row with the value undefined — a pending invitation, which has no account yet — renders a
     * dash rather than a switch, since there is nobody to restrict.
     */
    ageRestricted: (onToggle: (user: UserRow, ageRestricted: boolean) => void, pendingId?: string | null) =>
        ({
            key: "ageRestricted",
            header: <AgeRestrictedHeader />,
            cell: (user: UserRow) =>
                user.ageRestricted === undefined ? (
                    dash
                ) : (
                    <Switch
                        checked={user.ageRestricted}
                        disabled={pendingId === user.id}
                        aria-label={`Age restricted — ${user.firstName ?? user.email}`}
                        onCheckedChange={(next) => onToggle(user, next)}
                    />
                ),
        }) satisfies DataTableColumn<UserRow>,
} as const;

export { DataTable as UserTable };
